import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  VStack,
  useToast,
} from '@chakra-ui/react';
import axios from '../../axiosConfig';
import { useUser } from '../../contexts/UserContext';
import { getDisplayRoleLabel } from '../../utils/displayRole';
import { createUserInContext, parseCreateUserErrors } from './userCreationApi';
import { getOrganizationsForUserManagement } from './userOrganizationLookup';
import { getMutationError, getScopedUserBelongsToLabel, getUserMutationToast } from '../../utils/userMutationFeedback';
import {
  canCreateUsersInContext,
  filterConsultantsForPolicy,
  filterNetworksForPolicy,
  filterOrganizationsForPolicy,
  getUserCreationPolicy,
  resolveAllowedRolesByScope,
  resolveAllowedScopes,
} from '../../utils/userCreationPermissions';

const SCOPE_OPTIONS = [
  { value: 'app', label: 'Platform' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'network', label: 'Network' },
  { value: 'organization', label: 'School' },
];

const emptyRoleCatalog = {
  app: [],
  consultant: [],
  network: [],
  organization: [],
};

const trim = (value) => (typeof value === 'string' ? value.trim() : '');

const buildInitialState = () => ({
  first_name: '',
  last_name: '',
  email: '',
  scope: 'organization',
  role_name: '',
  consultant_id: '',
  network_id: '',
  organization_id: '',
});

const getDefaultScopeForContext = (contextType, allowedScopeOptions) => {
  const allowedScopes = Array.isArray(allowedScopeOptions)
    ? allowedScopeOptions.map((option) => option.value)
    : [];

  if (contextType && contextType !== 'hke' && allowedScopes.includes(contextType)) {
    return contextType;
  }

  return allowedScopes[0] || 'organization';
};

const matchesNetworkContext = (organization, networkId) => {
  const normalizedNetworkId = Number(networkId);
  if (!Number.isFinite(normalizedNetworkId) || normalizedNetworkId <= 0) return false;

  const candidateIds = []
    .concat(organization?.network_id)
    .concat(organization?.network?.id)
    .concat(Array.isArray(organization?.network_ids) ? organization.network_ids : [])
    .concat(Array.isArray(organization?.networks) ? organization.networks.map((network) => network?.id) : [])
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);

  return candidateIds.includes(normalizedNetworkId);
};

const normalizeRoleCatalogFromIndex = (payload) => {
  const catalog = { ...emptyRoleCatalog };
  if (!Array.isArray(payload)) return catalog;

  payload.forEach((row) => {
    const scope = trim(row?.scope).toLowerCase();
    const name = trim(row?.name).toLowerCase();
    if (!scope || !name || !catalog[scope]) return;
    if (!catalog[scope].includes(name)) catalog[scope].push(name);
  });

  return Object.keys(catalog).reduce((accumulator, scope) => {
    accumulator[scope] = [...catalog[scope]].sort();
    return accumulator;
  }, { ...emptyRoleCatalog });
};

const ContextUserCreateModal = ({
  isOpen,
  onClose,
  context,
  title = 'Create user',
  submitLabel = 'Create user',
  onCreated,
}) => {
  const toast = useToast();
  const { user } = useUser();
  const contextType = context?.type || 'hke';
  const creationPolicy = useMemo(() => getUserCreationPolicy(user), [user]);
  const canCreateUsers = useMemo(() => canCreateUsersInContext(user, context), [context, user]);
  const [availableRolesByScope, setAvailableRolesByScope] = useState({ ...emptyRoleCatalog });
  const [organizations, setOrganizations] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [consultants, setConsultants] = useState([]);

  const [formValues, setFormValues] = useState(buildInitialState());
  const [errors, setErrors] = useState({});
  const [errorMessages, setErrorMessages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const allowedScopeOptions = useMemo(
    () => SCOPE_OPTIONS.filter((option) => resolveAllowedScopes(creationPolicy, context).includes(option.value)),
    [context, creationPolicy]
  );
  const allowedRolesByScope = useMemo(
    () => resolveAllowedRolesByScope(creationPolicy, availableRolesByScope, context),
    [availableRolesByScope, context, creationPolicy]
  );
  const roleOptionsForScope = useMemo(
    () => allowedRolesByScope[formValues.scope] || [],
    [allowedRolesByScope, formValues.scope]
  );
  const filteredOrganizations = useMemo(() => {
    const scopedOrganizations = filterOrganizationsForPolicy(organizations, creationPolicy, context);
    if (scopedOrganizations.length > 0) return scopedOrganizations;
    if (contextType !== 'network' || !(Number(context?.id) > 0)) return scopedOrganizations;
    return organizations.filter((organization) => matchesNetworkContext(organization, context.id));
  }, [context, contextType, creationPolicy, organizations]);
  const filteredNetworks = useMemo(
    () => filterNetworksForPolicy(networks, creationPolicy, context),
    [context, creationPolicy, networks]
  );
  const filteredConsultants = useMemo(
    () => filterConsultantsForPolicy(consultants, creationPolicy),
    [consultants, creationPolicy]
  );
  const implicitConsultantId = useMemo(() => {
    if (formValues.scope !== 'consultant') return null;
    if (contextType === 'consultant' && Number(context?.id) > 0) return Number(context.id);
    if (filteredConsultants.length === 1) return Number(filteredConsultants[0].id);
    return null;
  }, [context?.id, contextType, filteredConsultants, formValues.scope]);
  const implicitNetworkId = useMemo(() => {
    if (formValues.scope !== 'network') return null;
    if (contextType === 'network' && Number(context?.id) > 0) return Number(context.id);
    if (filteredNetworks.length === 1) return Number(filteredNetworks[0].id);
    return null;
  }, [context?.id, contextType, filteredNetworks, formValues.scope]);
  const implicitOrganizationId = useMemo(() => {
    if (formValues.scope !== 'organization') return null;
    if (contextType === 'organization' && Number(context?.id) > 0) return Number(context.id);
    if (filteredOrganizations.length === 1) return Number(filteredOrganizations[0].id);
    return null;
  }, [context?.id, contextType, filteredOrganizations, formValues.scope]);
  const showConsultantSelector = formValues.scope === 'consultant' && !implicitConsultantId;
  const showNetworkSelector = formValues.scope === 'network' && !implicitNetworkId;
  const showOrganizationSelector = formValues.scope === 'organization' && !implicitOrganizationId;
  const membershipContextLabel = useMemo(() => {
    if (formValues.scope === 'consultant') {
      const consultant = filteredConsultants.find((entry) => Number(entry.id) === Number(implicitConsultantId || formValues.consultant_id));
      return getScopedUserBelongsToLabel({ type: 'consultant', id: implicitConsultantId || formValues.consultant_id }, { consultantName: consultant?.name });
    }
    if (formValues.scope === 'network') {
      const network = filteredNetworks.find((entry) => Number(entry.id) === Number(implicitNetworkId || formValues.network_id));
      return getScopedUserBelongsToLabel({ type: 'network', id: implicitNetworkId || formValues.network_id }, { networkName: network?.name });
    }
    if (formValues.scope === 'organization') {
      const organization = filteredOrganizations.find(
        (entry) => Number(entry.id) === Number(implicitOrganizationId || formValues.organization_id)
      );
      return getScopedUserBelongsToLabel(
        { type: 'organization', id: implicitOrganizationId || formValues.organization_id },
        { organizationName: organization?.name }
      );
    }
    return getScopedUserBelongsToLabel({ type: 'hke' });
  }, [
    filteredConsultants,
    filteredNetworks,
    filteredOrganizations,
    formValues.consultant_id,
    formValues.network_id,
    formValues.organization_id,
    formValues.scope,
    implicitConsultantId,
    implicitNetworkId,
    implicitOrganizationId,
  ]);
  const defaultScope = useMemo(
    () => getDefaultScopeForContext(contextType, allowedScopeOptions),
    [allowedScopeOptions, contextType]
  );

  useEffect(() => {
    if (!isOpen) return;
    setFormValues({ ...buildInitialState(), scope: defaultScope });
    setErrors({});
    setErrorMessages([]);
  }, [contextType, defaultScope, isOpen]);

  useEffect(() => {
    if (!isOpen || !canCreateUsers) return;

    let isMounted = true;

    const load = async () => {
      try {
        const [roleCatalogRes, orgRes, netRes, conRes] = await Promise.allSettled([
          axios.get('/roles'),
          getOrganizationsForUserManagement(context),
          axios.get('/networks'),
          axios.get('/consultants'),
        ]);

        if (!isMounted) return;

        setAvailableRolesByScope(normalizeRoleCatalogFromIndex(roleCatalogRes.status === 'fulfilled' ? roleCatalogRes.value?.data : null));
        setOrganizations(orgRes.status === 'fulfilled' && Array.isArray(orgRes.value) ? orgRes.value : []);
        setNetworks(Array.isArray(netRes.value?.data) ? netRes.value?.data : netRes.value?.data?.networks || []);
        setConsultants(Array.isArray(conRes.value?.data) ? conRes.value?.data : conRes.value?.data?.consultants || []);
      } catch (error) {
        if (!isMounted) return;
        setAvailableRolesByScope({ ...emptyRoleCatalog });
        setOrganizations([]);
        setNetworks([]);
        setConsultants([]);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [canCreateUsers, context, isOpen]);

  useEffect(() => {
    if (allowedScopeOptions.some((option) => option.value === formValues.scope)) return;
    setFormValues((prev) => ({ ...prev, scope: allowedScopeOptions[0]?.value || 'organization', role_name: '' }));
  }, [allowedScopeOptions, formValues.scope]);

  useEffect(() => {
    if (roleOptionsForScope.includes(formValues.role_name)) return;
    setFormValues((prev) => ({ ...prev, role_name: roleOptionsForScope[0] || '' }));
  }, [formValues.role_name, roleOptionsForScope]);

  useEffect(() => {
    if (!implicitConsultantId) return;
    setFormValues((prev) => ({ ...prev, consultant_id: String(implicitConsultantId) }));
  }, [implicitConsultantId]);

  useEffect(() => {
    if (!implicitNetworkId) return;
    setFormValues((prev) => ({ ...prev, network_id: String(implicitNetworkId) }));
  }, [implicitNetworkId]);

  useEffect(() => {
    if (!implicitOrganizationId) return;
    setFormValues((prev) => ({ ...prev, organization_id: String(implicitOrganizationId) }));
  }, [implicitOrganizationId]);

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const nextErrors = {};
    if (!trim(formValues.first_name)) nextErrors.first_name = 'First name is required';
    if (!trim(formValues.last_name)) nextErrors.last_name = 'Last name is required';
    if (!trim(formValues.email)) nextErrors.email = 'Email is required';
    if (!trim(formValues.scope)) nextErrors.scope = 'Scope is required';
    if (!trim(formValues.role_name)) nextErrors.role_name = 'Role is required';
    if (formValues.scope === 'consultant' && !trim(implicitConsultantId ? String(implicitConsultantId) : formValues.consultant_id)) {
      nextErrors.consultant_id = 'Consultant is required';
    }
    if (formValues.scope === 'network' && !trim(implicitNetworkId ? String(implicitNetworkId) : formValues.network_id)) {
      nextErrors.network_id = 'Network is required';
    }
    if (formValues.scope === 'organization' && !trim(implicitOrganizationId ? String(implicitOrganizationId) : formValues.organization_id)) {
      nextErrors.organization_id = 'School is required';
    }
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    setErrorMessages([]);
    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      first_name: trim(formValues.first_name),
      last_name: trim(formValues.last_name),
      email: trim(formValues.email),
      scope: trim(formValues.scope),
      role_name: trim(formValues.role_name),
      consultant_id: formValues.scope === 'consultant' ? Number(implicitConsultantId || formValues.consultant_id) : null,
      network_id: formValues.scope === 'network' ? Number(implicitNetworkId || formValues.network_id) : null,
      organization_id: formValues.scope === 'organization' ? Number(implicitOrganizationId || formValues.organization_id) : null,
    };

    setIsSubmitting(true);
    try {
      const response = await createUserInContext(context, payload);
      toast(getUserMutationToast('create', { description: `Access granted in ${membershipContextLabel}.` }));
      if (onCreated) {
        await onCreated(response, payload);
      }
      onClose();
    } catch (error) {
      const parsed = parseCreateUserErrors(error);
      const mutationError = getMutationError(error);
      setErrors((prev) => ({ ...prev, ...parsed.fieldErrors }));
      setErrorMessages(parsed.messages.length > 0 ? parsed.messages : [mutationError.message]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit}>
          <ModalBody>
            {!canCreateUsers ? (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <AlertDescription>You do not have permission to create users in this area.</AlertDescription>
              </Alert>
            ) : (
              <VStack spacing={3}>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>
                    New users created here will belong to {membershipContextLabel}.
                  </AlertDescription>
                </Alert>

                {errorMessages.length > 0 && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription>{errorMessages.join(' ')}</AlertDescription>
                  </Alert>
                )}

                <FormControl isRequired isInvalid={Boolean(errors.first_name)}>
                  <FormLabel>First name</FormLabel>
                  <Input
                    value={formValues.first_name}
                    onChange={(event) => handleFieldChange('first_name', event.target.value)}
                  />
                  <FormErrorMessage>{errors.first_name}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.last_name)}>
                  <FormLabel>Last name</FormLabel>
                  <Input
                    value={formValues.last_name}
                    onChange={(event) => handleFieldChange('last_name', event.target.value)}
                  />
                  <FormErrorMessage>{errors.last_name}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.email)}>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    value={formValues.email}
                    onChange={(event) => handleFieldChange('email', event.target.value)}
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.scope)}>
                  <FormLabel>Scope</FormLabel>
                  <Select
                    value={formValues.scope}
                    onChange={(event) =>
                      setFormValues((prev) => ({
                        ...prev,
                        scope: event.target.value,
                        role_name: '',
                        consultant_id: '',
                        network_id: '',
                        organization_id: '',
                      }))
                    }
                  >
                    {allowedScopeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.scope}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.role_name)}>
                  <FormLabel>Role</FormLabel>
                  <Select
                    value={formValues.role_name}
                    onChange={(event) => handleFieldChange('role_name', event.target.value)}
                    placeholder={roleOptionsForScope.length > 0 ? 'Select role' : 'No roles available'}
                    isDisabled={roleOptionsForScope.length === 0}
                  >
                    {roleOptionsForScope.map((roleName) => (
                      <option key={roleName} value={roleName}>
                        {getDisplayRoleLabel(roleName)}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>{errors.role_name}</FormErrorMessage>
                </FormControl>

                {showConsultantSelector ? (
                  <FormControl isRequired isInvalid={Boolean(errors.consultant_id)}>
                    <FormLabel>Consultant</FormLabel>
                    <Select
                      value={formValues.consultant_id}
                      onChange={(event) => handleFieldChange('consultant_id', event.target.value)}
                    >
                      <option value="">Select consultant</option>
                      {filteredConsultants.map((consultant) => (
                        <option key={consultant.id} value={consultant.id}>
                          {consultant.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.consultant_id}</FormErrorMessage>
                  </FormControl>
                ) : null}

                {showNetworkSelector ? (
                  <FormControl isRequired isInvalid={Boolean(errors.network_id)}>
                    <FormLabel>Network</FormLabel>
                    <Select
                      value={formValues.network_id}
                      onChange={(event) => handleFieldChange('network_id', event.target.value)}
                    >
                      <option value="">Select network</option>
                      {filteredNetworks.map((network) => (
                        <option key={network.id} value={network.id}>
                          {network.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.network_id}</FormErrorMessage>
                  </FormControl>
                ) : null}

                {showOrganizationSelector ? (
                  <FormControl isRequired isInvalid={Boolean(errors.organization_id)}>
                    <FormLabel>School</FormLabel>
                    <Select
                      value={formValues.organization_id}
                      onChange={(event) => handleFieldChange('organization_id', event.target.value)}
                    >
                      <option value="">Select school</option>
                      {filteredOrganizations.map((organization) => (
                        <option key={organization.id} value={organization.id}>
                          {organization.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage>{errors.organization_id}</FormErrorMessage>
                  </FormControl>
                ) : null}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button type="submit" colorScheme="blue" isLoading={isSubmitting} isDisabled={!canCreateUsers}>{submitLabel}</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ContextUserCreateModal;
